if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      _ogStateCache = null;

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        // Subscribe to variant change events to update the price in the add to cart button
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.updateButtonPrice.bind(this));

        // Listen for selling plan changes (subscription selection)
        this.initSellingPlanListener();

        this.initQuantityHandlers();

        // Initialize button price with the initially selected variant.
        // OrderGroove opt-in state is applied via initSellingPlanListener's callback when OG is ready; no delay needed.
        this.initializeButtonPrice();

        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      initializeButtonPrice() {
        const initialVariant = this.getSelectedVariant(this, { includeSingleVariantFallback: true });
        if (initialVariant) {
          this.updateButtonPrice({ data: { variant: initialVariant } });
        }
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.variantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll('.scroll-trigger')
            .forEach((element) => element.classList.add('scroll-trigger--cancel')),
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      addVariantClickHandlers() {
        // Get all variant options buttons and dropdowns
        const variantButtons = this.querySelectorAll('.product-form__input--pill .product-form__radio');
        const swatchButtons = this.querySelectorAll('.product-form__input--swatch .product-form__radio');
        const dropdowns = this.querySelectorAll('.product-form__input--dropdown select');

        // Add event listeners to buttons (pill style)
        variantButtons.forEach((button) => {
          button.addEventListener('click', this.handleVariantButtonClick.bind(this));
        });

        // Add event listeners to swatch buttons
        swatchButtons.forEach((swatch) => {
          swatch.addEventListener('click', this.handleVariantButtonClick.bind(this));
        });

        // Add event listeners to dropdowns
        dropdowns.forEach((dropdown) => {
          dropdown.addEventListener('change', this.handleVariantDropdownChange.bind(this));
        });
      }

      // Handle variant button clicks
      handleVariantButtonClick(event) {
        const target = event.currentTarget;
        const selectedOptionValues = this.getSelectedOptionValues();

        // Publish the event
        publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
          data: {
            event,
            target,
            selectedOptionValues,
          },
        });
      }

      // Handle variant dropdown changes
      handleVariantDropdownChange(event) {
        const target = event.currentTarget;
        const selectedOptionValues = this.getSelectedOptionValues();

        // Publish the event
        publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
          data: {
            event,
            target,
            selectedOptionValues,
          },
        });
      }

      // Get all currently selected option values
      getSelectedOptionValues() {
        const selectedOptionValues = [];
        const variantSelects = this.querySelector('variant-selects');

        if (variantSelects) {
          // Get values from pill/swatch buttons
          const activeButtons = variantSelects.querySelectorAll('.product-form__radio:checked');
          activeButtons.forEach((button) => {
            selectedOptionValues.push(button.value);
          });

          // Get values from dropdowns
          const dropdowns = variantSelects.querySelectorAll('.product-form__input--dropdown select');
          dropdowns.forEach((dropdown) => {
            if (dropdown.value) {
              selectedOptionValues.push(dropdown.value);
            }
          });
        }

        return selectedOptionValues;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut,
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      initSellingPlanListener() {
        // Use OrderGroove that should be on window
        if (window.OG && typeof window.OG.addOptinChangedCallback === 'function') {
          const handleOptinChange = (changedState) => {
            this.updateButtonPriceFromSellingPlan(Boolean(changedState.optedIn));
          };

          window.OG.addOptinChangedCallback(handleOptinChange);
        }
      }

      updateButtonPriceFromSellingPlan(optedIn = null) {
        const submitButton = this.querySelector('.product-form__submit');
        if (!submitButton) {
          return;
        }

        const priceSpan = submitButton.querySelector('.variant-price');
        if (!priceSpan) {
          return;
        }

        const subscriptionPrices = submitButton.dataset.subscriptionPrices;
        const regularPrice = parseInt(submitButton.dataset.regularPrice) || 0;
        const compareAtPrice = parseInt(submitButton.dataset.compareAtPrice) || 0;

        const noSubscriptionPrice =
          !subscriptionPrices || subscriptionPrices === '{}' || subscriptionPrices.trim() === '';
        if (optedIn === false || noSubscriptionPrice) {
          this.updateButtonPriceDisplay(priceSpan, regularPrice, compareAtPrice);
          return;
        }

        let subscriptionPricesByPlanId = {};
        try {
          const cleanedPrices = subscriptionPrices.trim();
          subscriptionPricesByPlanId = JSON.parse(cleanedPrices);
        } catch (e) {
          // Fall back to regular price if parsing fails
          this.updateButtonPriceDisplay(priceSpan, regularPrice, compareAtPrice);
          return;
        }

        // If optedIn is explicitly true, find the first available subscription price
        if (optedIn === true) {
          const firstPlanId = Object.keys(subscriptionPricesByPlanId)[0];
          if (firstPlanId && subscriptionPricesByPlanId[firstPlanId]) {
            const subscriptionPrice = subscriptionPricesByPlanId[firstPlanId];
            this.updateButtonPriceDisplay(priceSpan, subscriptionPrice, regularPrice);
            return;
          }
        }
      }

      formatMoney(amount) {
        return window.Shopify?.formatMoney
          ? window.Shopify.formatMoney(amount, window.theme?.moneyFormat)
          : `$${(amount / 100).toString().replace(/\.00$/, '')}`;
      }

      updateButtonPriceDisplay(priceSpan, price, compareAtPrice = 0) {
        const formattedPrice = this.formatMoney(price);
        const formattedComparePrice = compareAtPrice > 0 ? this.formatMoney(compareAtPrice) : null;
        const regularPriceLabel = window.variantStrings?.regularPrice ?? 'Regular price';
        const salePriceLabel = window.variantStrings?.salePrice ?? 'Sale price';

        if (compareAtPrice > price) {
          priceSpan.innerHTML = `
            <span class="visually-hidden visually-hidden--inline">${regularPriceLabel}</span>
            <s class="product-form__compare-at-price">${formattedComparePrice}</s>
            <span class="visually-hidden visually-hidden--inline">${salePriceLabel}</span>
            <span>${formattedPrice}</span>
          `;
        } else {
          priceSpan.textContent = formattedPrice;
        }
      }

      updateButtonPrice({ data: { variant } }) {
        if (!variant) {
          return;
        }

        const submitButton = this.querySelector('.product-form__submit');
        if (!submitButton) {
          return;
        }

        const priceSpan = submitButton.querySelector('.variant-price');
        if (!priceSpan) {
          return;
        }

        // Store variant prices on button so updateButtonPriceFromSellingPlan can show compare-at when showing regular price
        submitButton.dataset.regularPrice = variant.price || 0;
        submitButton.dataset.compareAtPrice = variant.compare_at_price || 0;

        let optedIn = null;
        if (window.OG) {
          // Update subscription prices if variant has selling plan allocations
          if (variant.selling_plan_allocations && variant.selling_plan_allocations.length > 0) {
            const subscriptionPricesByPlanId = {};
            variant.selling_plan_allocations.forEach((allocation) => {
              subscriptionPricesByPlanId[allocation.selling_plan_id] =
                allocation.per_delivery_price || allocation.price;
            });
            submitButton.dataset.subscriptionPrices = JSON.stringify(subscriptionPricesByPlanId);
          } else {
            submitButton.dataset.subscriptionPrices = '{}';
          }

          // Check if a selling plan is currently selected and update price accordingly.
          // Use OG_STATE from localStorage (optedin/optedout) when available. Cache parsed result until OG_STATE changes.
          try {
            const ogStateRaw = localStorage.getItem('OG_STATE');
            if (ogStateRaw) {
              if (!this._ogStateCache || this._ogStateCache.raw !== ogStateRaw) {
                const ogState = JSON.parse(ogStateRaw);
                const optedInList = Array.isArray(ogState?.optedin) ? ogState.optedin : [];
                const optedOutList = Array.isArray(ogState?.optedout) ? ogState.optedout : [];
                this._ogStateCache = {
                  raw: ogStateRaw,
                  optedInIds: optedInList
                    .filter((item) => item != null && typeof item === 'object' && 'id' in item)
                    .map((item) => String(item.id)),
                  optedOutIds: optedOutList
                    .filter((item) => item != null && typeof item === 'object' && 'id' in item)
                    .map((item) => String(item.id)),
                };
              }
              const { optedInIds, optedOutIds } = this._ogStateCache;
              const variantId = String(variant.id);
              if (optedInIds.includes(variantId)) {
                optedIn = true;
              } else if (optedOutIds.includes(variantId)) {
                optedIn = false;
              } else {
                optedIn = false;
              }
            }
          } catch (e) {
            this._ogStateCache = null;
            console.warn('[product-info] OG_STATE read/parse failed', e);
          }
        }
        this.updateButtonPriceFromSellingPlan(optedIn);
      }

      getSelectedVariant(productInfoNode, options = {}) {
        const { includeSingleVariantFallback = false } = options;
        // Multi-variant: variant-selects [data-selected-variant]. Single-variant: [data-selected-variant] only (when fallback requested).
        let scriptElement = productInfoNode.querySelector('variant-selects [data-selected-variant]');
        if (!scriptElement && includeSingleVariantFallback) {
          scriptElement = productInfoNode.querySelector('[data-selected-variant]');
        }
        const selectedVariant = scriptElement?.innerHTML;
        try {
          return selectedVariant ? JSON.parse(selectedVariant) : null;
        } catch (e) {
          console.error('Error parsing selected variant:', e);
          return null;
        }
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`,
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }]),
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]'),
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`),
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true,
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products',
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list',
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    },
  );
}
